import { useEffect, useMemo, useRef, useState } from "react";

import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import { cloneDeep, groupBy, sortBy, uniq } from "lodash";

import { GenericTooltip } from "@components/tooltip-container";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { restrictToParentElement } from "@dnd-kit/modifiers";
import { SortableContext, arrayMove } from "@dnd-kit/sortable";
import { useLabelWizard } from "@features/breakdown-v2/label-rule-wizard/hooks";
import { ordinal } from "@features/data-export/components/schedule-configurator/utils";
import { BreakdownFilter } from "@features/overview-v2/components/modals/tile-modals/breakdown-filter";
import { BreakdownSelect } from "@features/overview-v2/components/modals/tile-modals/breakdown-select";
import {
  FloatingPortal,
  flip,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from "@floating-ui/react";
import { useDesignElementManager } from "@hooks/use-design-element-manager";
import { useFuzzySearch } from "@hooks/use-fuzzy-search";
import {
  type BreakdownGroupDescriptor,
  type SegmentLevel,
} from "@north-beam/nb-common";
import { PrimaryButton, TertiaryButton } from "@shared/buttons";
import { useVirtualizer } from "@tanstack/react-virtual";

import { ButtonGroup } from "../button-group";

import { Option } from "./option";

const LEVEL_TO_LABEL = {
  all: "All",
  campaign: "Campaigns",
  adset: "Adsets",
  ad: "Ads",
};

const FREQUENTLY_USED_PLATFORMS = [
  "Facebook Ads",
  "Google Ads",
  "TikTok",
  "YouTube Ads",
  "Microsoft Ads",
];

interface Props {
  breakdownGroup: BreakdownGroupDescriptor;
  level: SegmentLevel;
  filters: string[][];
  onChange: (level: SegmentLevel, filters: string[][]) => void;
}

export const FilterSelector = ({
  breakdownGroup,
  level,
  filters,
  onChange,
}: Props) => {
  const { designElementManager } = useDesignElementManager();
  const { labelFilters } = useLabelWizard();

  const scrollElRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isAboutToDelete, setIsAboutToDelete] = useState(false);

  const [localLevel, setLocalLevel] = useState(level);
  const [localFilters, setLocalFilters] = useState<string[][]>([]);
  const [breakdownKeys, setBreakdownKeys] = useState<string[]>([]);
  const [filtersType, setFiltersType] = useState<
    "all" | "platforms" | "custom"
  >("all");

  const breakdownOptions = useMemo(
    () =>
      uniq(
        labelFilters
          .filter(
            (e) =>
              e.labelKey !== breakdownGroup.name &&
              e.labelKey !== breakdownGroup.nameBeforeEdit,
          )
          .map((e) => e.labelKey),
      ),
    [labelFilters, breakdownGroup],
  );

  const breakdownFilterOptions = useMemo(() => {
    const group = groupBy(labelFilters, "labelKey");
    return Object.entries(group).map(([key, filters]) => ({
      key,
      values: filters.map((e) => e.labelValue),
    }));
  }, [labelFilters]);

  const availableLabelFilters = useMemo(
    () => breakdownOptions.filter((e) => !breakdownKeys.includes(e)),
    [breakdownOptions, breakdownKeys],
  );

  const platformFilters = useMemo(
    () => labelFilters.filter((e) => e.labelKey === "Platform (Northbeam)"),
    [labelFilters],
  );

  const [filteredPlatformFilters, { search, setSearch, clearSearch }] =
    useFuzzySearch(platformFilters, {
      searchKeys: ["labelValue"],
    });

  const sensors = useSensors(useSensor(PointerSensor));

  const changeMenuOpen = (value: boolean) => {
    if (value) {
      clearSearch();
      setLocalLevel(level);
      setLocalFilters(cloneDeep(filters));
      setFiltersType("all");

      if (filters.length > 0) {
        if (
          filters[0].length === 1 &&
          filters[0][0].split(":")[0] === "Platform (Northbeam)"
        ) {
          setFiltersType("platforms");
        } else {
          setBreakdownKeys(filters[0].map((v) => v.split(":")[0]));
          setFiltersType("custom");
        }
      }
    }

    setIsOpen(value);
  };

  const {
    refs,
    floatingStyles,
    context,
    update: updateFloating,
  } = useFloating({
    open: isOpen,
    onOpenChange: changeMenuOpen,
    transform: false,
    placement: "right",
    middleware: [flip(), shift()],
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  useEffect(
    () => updateFloating(),
    [search, filtersType, breakdownKeys, localFilters, updateFloating],
  );

  const sortedPlatformFilters = useMemo(
    () => sortBy([...filteredPlatformFilters], "labelValue"),
    [filteredPlatformFilters],
  );

  const frequentlyUsedPlatformFilters = useMemo(() => {
    return sortedPlatformFilters.filter((e) =>
      FREQUENTLY_USED_PLATFORMS.includes(e.labelValue),
    );
  }, [sortedPlatformFilters]);

  const rowVirtualizer = useVirtualizer({
    count: sortedPlatformFilters.length,
    getScrollElement: () => scrollElRef.current,
    estimateSize: () => 36,
    overscan: 5,
    paddingStart:
      sortedPlatformFilters.length === 0
        ? 0
        : search.length === 0
        ? 48 + 36 * frequentlyUsedPlatformFilters.length
        : 16,
  });

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      const oldIndex = breakdownKeys.findIndex((e) => e === active.id);
      const newIndex = breakdownKeys.findIndex((e) => e === over?.id);

      const newOrdering = arrayMove(breakdownKeys, oldIndex, newIndex);
      setBreakdownKeys(newOrdering);
      setLocalFilters([]);
    }
  };

  const selectedLocalPlatformFilters = useMemo(() => {
    if (localFilters.length === 0) {
      return [];
    }

    if (localFilters[0].length > 1) {
      return [];
    }

    if (localFilters[0][0].split(":")[0] !== "Platform (Northbeam)") {
      return [];
    }

    const ids = localFilters.map((e) => e[0]);
    return labelFilters.filter((e) => ids.includes(e.id));
  }, [localFilters, labelFilters]);

  const selectedPlatformFilters = useMemo(() => {
    if (filters.length === 0) {
      return [];
    }

    if (filters[0].length > 1) {
      return [];
    }

    if (filters[0][0].split(":")[0] !== "Platform (Northbeam)") {
      return [];
    }

    const ids = filters.map((e) => e[0]);
    return labelFilters.filter((e) => ids.includes(e.id));
  }, [filters, labelFilters]);

  const visiblePlatformFilters = selectedPlatformFilters.slice(
    0,
    selectedPlatformFilters.length > 3 ? 2 : 3,
  );
  const restPlatformFilters = selectedPlatformFilters.slice(3);

  const isDefault = level === "all" && filters.length === 0;

  return (
    <div className="shrink-0">
      <button
        className={clsx("flex items-center", {
          "button tertiary-button sm gap-2 font-bold": isDefault,
          "text-xs rounded border h-8 pl-2 pr-1": !isDefault,
          "bg-cosmic text-white": isDefault && isOpen,
          "bg-cosmic-5 border-cosmic-1 hover:border-cosmic":
            !isDefault && !isOpen && !isAboutToDelete,
          "bg-cosmic border-cosmic text-white": !isDefault && isOpen,
          "border-mars bg-mars-2": isAboutToDelete && !isOpen,
        })}
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        {isDefault ? (
          <>
            <i
              className={clsx("fa-regular fa-filter", {
                "text-cosmic": !isOpen,
                "text-white": isOpen,
              })}
            />
            APPLY TO
          </>
        ) : (
          <>
            <div
              className={clsx("font-bold", {
                "text-cosmic": !isOpen,
                "text-white": isOpen,
              })}
            >
              Applies to
            </div>
            <div className="ml-2">{LEVEL_TO_LABEL[level]}</div>
            {filters.length > 0 && (
              <GenericTooltip
                className="ml-2"
                noInfoCircle
                content={
                  <div className="space-y-1.5 text-xs">
                    {filters.map((filter) => {
                      return (
                        <div
                          key={JSON.stringify(filter)}
                          className="flex items-center gap-1.5 h-[20px]"
                        >
                          {filter.map((id, i) => {
                            const [key, value] = id.split(":");

                            return (
                              <span
                                key={id}
                                className="flex items-center gap-1.5 h-[20px]"
                              >
                                <span className="flex items-center gap-1">
                                  {key === "Platform (Northbeam)" &&
                                    designElementManager
                                      .getDesignElement(value)
                                      .getComponent({ size: "20px" })}
                                  {value}
                                </span>
                                {i < filter.length - 1 && <span>/</span>}
                              </span>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                }
              >
                {selectedPlatformFilters.length > 0 ? (
                  <div className="flex">
                    {visiblePlatformFilters.map((e) => (
                      <span key={e.id} className="mr-[-4px]">
                        {designElementManager
                          .getDesignElement(e.labelValue)
                          .getComponent({ size: "18px" })}
                      </span>
                    ))}
                    {restPlatformFilters.length > 0 && (
                      <span
                        className={clsx(
                          "w-[18px] h-[18px] rounded-full bg-gray-3 border border-gray-2 text-[9px]",
                          "text-interstellar flex items-center justify-center",
                        )}
                      >
                        +{restPlatformFilters.length + 1}
                      </span>
                    )}
                  </div>
                ) : (
                  <div
                    className={clsx(
                      "bg-interstellar-3 border border-gray rounded h-[20px] px-2 text-xxs",
                      "flex items-center gap-1 text-interstellar",
                    )}
                  >
                    <span>{filters.length}</span>
                    <span>
                      {filters.length === 1 ? "breakdown" : "breakdowns"}
                    </span>
                  </div>
                )}
              </GenericTooltip>
            )}
            <GenericTooltip
              className="ml-1"
              overlayClassName="pointer-events-none"
              noInfoCircle
              content="Delete filters"
            >
              <TertiaryButton
                className={clsx(
                  "w-6 h-6 p-0 ml-1 text-base flex items-center justify-center",
                  "rounded bg-transparent hover:bg-white hover:text-mars group/close-button",
                )}
                onMouseEnter={() => setIsAboutToDelete(true)}
                onMouseLeave={() => setIsAboutToDelete(false)}
                onClick={(e) => {
                  e.preventDefault();
                  onChange("all", []);
                  setIsAboutToDelete(false);
                  setIsOpen(false);
                }}
              >
                <i
                  className={clsx(
                    "fa-regular fa-sharp fa-times group-hover/close-button:text-mars",
                    { "text-white": isOpen },
                  )}
                ></i>
              </TertiaryButton>
            </GenericTooltip>
          </>
        )}
      </button>
      <FloatingPortal>
        <AnimatePresence>
          {isOpen && (
            <motion.div
              transition={{ ease: "easeOut", duration: 0.2 }}
              initial={{ opacity: 0, translateY: "0.25rem" }}
              animate={{ opacity: 1, translateY: 0 }}
              exit={{ opacity: 0, translateY: "0.25rem" }}
              ref={refs.setFloating}
              style={floatingStyles}
              className={clsx(
                "absolute z-[2005]",
                "ml-2 mr-2 z-10 bg-white rounded-lg elevation-1 text-sm pt-3 w-[340px]",
              )}
              {...getFloatingProps()}
            >
              <div className="mx-4 mb-3 pb-3 border-b border-gray-2">
                <ButtonGroup
                  options={[
                    { label: LEVEL_TO_LABEL["all"], value: "all" },
                    { label: LEVEL_TO_LABEL["campaign"], value: "campaign" },
                    { label: LEVEL_TO_LABEL["adset"], value: "adset" },
                    { label: LEVEL_TO_LABEL["ad"], value: "ad" },
                  ]}
                  value={localLevel}
                  onChange={(value) => setLocalLevel(value)}
                />
              </div>
              <div className="px-4 mb-2">
                <h5 className="text-xs tracking-wider mb-2">
                  FILTER BY EXISTING BREAKDOWN
                </h5>
                <div className="text-xs mb-4">
                  <ButtonGroup
                    options={[
                      { label: "All", value: "all" },
                      { label: "Platforms", value: "platforms" },
                      { label: "Custom", value: "custom" },
                    ]}
                    value={filtersType}
                    onChange={(value) => {
                      setFiltersType(value);
                      setLocalFilters(cloneDeep(filters));
                      if (value === "custom") {
                        setBreakdownKeys(["Platform (Northbeam)"]);
                      } else {
                        setBreakdownKeys([]);
                      }
                    }}
                  />
                </div>
              </div>
              {filtersType === "platforms" && (
                <div className="pb-4">
                  <div className="nb-input-container relative px-4">
                    <input
                      className="nb-input pr-8"
                      type="text"
                      placeholder="Search..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                    <i
                      className={clsx(
                        "fa-regular fa-sharp fa-magnifying-glass text-[0.9rem] text-primary",
                        "w-4 absolute right-[24px] top-[10px]",
                      )}
                    />
                  </div>
                  {sortedPlatformFilters.length === 0 && (
                    <div className="px-4 py-2 text-disabled">
                      NO RESULTS FOUND
                    </div>
                  )}
                  <div
                    ref={scrollElRef}
                    className="max-h-[360px] overflow-y-auto floatingui-scroll mt-3"
                  >
                    <div
                      style={{
                        position: "relative",
                        height: `${rowVirtualizer.getTotalSize()}px`,
                      }}
                    >
                      {frequentlyUsedPlatformFilters.length > 0 &&
                        search.length === 0 && (
                          <>
                            <div className="text-xxs font-bold px-4 mb-1">
                              Frequently used
                            </div>
                            <div className="mb-4">
                              {frequentlyUsedPlatformFilters.map((e) => (
                                <Option
                                  key={e.id}
                                  value={selectedLocalPlatformFilters
                                    .map((f) => f.id)
                                    .includes(e.id)}
                                  label={e.labelValue}
                                  onChange={(value) =>
                                    value
                                      ? setLocalFilters([
                                          ...cloneDeep(localFilters),
                                          [e.id],
                                        ])
                                      : setLocalFilters(
                                          cloneDeep(localFilters).filter(
                                            (f) => f[0] !== e.id,
                                          ),
                                        )
                                  }
                                />
                              ))}
                            </div>
                          </>
                        )}
                      {sortedPlatformFilters.length > 0 && (
                        <div className="text-xxs font-bold px-4">
                          All Platforms
                        </div>
                      )}
                      {rowVirtualizer.getVirtualItems().map((e) => {
                        const row = sortedPlatformFilters[e.index];

                        return (
                          <Option
                            key={e.key}
                            className="absolute top-0 left-0"
                            style={{
                              transform: `translateY(${e.start}px)`,
                            }}
                            value={selectedLocalPlatformFilters
                              .map((f) => f.id)
                              .includes(row.id)}
                            label={row.labelValue}
                            onChange={(value) =>
                              value
                                ? setLocalFilters([
                                    ...cloneDeep(localFilters),
                                    [row.id],
                                  ])
                                : setLocalFilters(
                                    cloneDeep(localFilters).filter(
                                      (f) => f[0] !== row.id,
                                    ),
                                  )
                            }
                          />
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
              {filtersType === "custom" && (
                <div className="pb-4 px-4 space-y-2">
                  {breakdownKeys.length > 0 && (
                    <div className="flex flex-col gap-2">
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        modifiers={[restrictToParentElement]}
                        onDragEnd={handleDragEnd}
                        autoScroll={false}
                      >
                        <SortableContext items={breakdownKeys}>
                          {breakdownKeys.map((breakdown, i) => (
                            <BreakdownSelect
                              key={breakdown}
                              options={breakdownOptions.filter(
                                (e) =>
                                  !breakdownKeys.includes(e) || e === breakdown,
                              )}
                              value={breakdown}
                              position={i + 1}
                              showPosition={breakdownKeys.length > 1}
                              onChange={(value) => {
                                setBreakdownKeys(
                                  breakdownKeys.map((e) =>
                                    e === breakdown ? value : e,
                                  ),
                                );
                                setLocalFilters([]);
                              }}
                              onDelete={() => {
                                setBreakdownKeys(
                                  breakdownKeys.filter((e) => e !== breakdown),
                                );
                                setLocalFilters([]);
                              }}
                            />
                          ))}
                        </SortableContext>
                      </DndContext>
                    </div>
                  )}
                  {availableLabelFilters.length !== 0 && (
                    <BreakdownSelect
                      options={availableLabelFilters}
                      placeholder={
                        breakdownKeys.length > 0
                          ? `${ordinal(breakdownKeys.length + 1)} breakdown...`
                          : "Select..."
                      }
                      onChange={(value) => {
                        setBreakdownKeys([...breakdownKeys, value]);
                        setLocalFilters([]);
                      }}
                    />
                  )}
                  {breakdownKeys.length !== 0 && (
                    <BreakdownFilter
                      className="mt-3"
                      options={breakdownFilterOptions}
                      breakdownKeys={breakdownKeys}
                      breakdownValues={localFilters.map((lf) =>
                        lf.map((v) => v.split(":")[1]),
                      )}
                      onChange={(values) => {
                        const ids = values.map((vals) =>
                          vals.map((v, i) => `${breakdownKeys[i]}:${v}`),
                        );
                        setLocalFilters(ids);
                      }}
                    />
                  )}
                </div>
              )}
              <PrimaryButton
                className={clsx(
                  "block w-full font-bold rounded-none rounded-b-lg py-[18px]",
                  "bg-cosmic text-white hover:bg-cosmic+1",
                )}
                onClick={() => {
                  changeMenuOpen(false);
                  onChange(localLevel, localFilters);
                }}
              >
                APPLY
              </PrimaryButton>
            </motion.div>
          )}
        </AnimatePresence>
      </FloatingPortal>
    </div>
  );
};
